/* =====================================================================
   PIXI RENDERER — WebGL-рендер МИРА и ТЬМЫ (Фаза 2).
   Рисует под Canvas2D-оверлеем (UI.render): grid, враги, игрок, снаряды,
   частицы, эффекты, пятна Якорей, тьму/свет (fullscreen-меши-шейдеры),
   глинты. HUD/меню/числа урона остаются на 2D-оверлее (см. ui.ts).

   Камера = смещение мировых контейнеров. Тьма/ореол — экранные fullscreen
   clip-space меши с кастомными шейдерами (alpha-over тьма + аддитивный ореол),
   читают Game.shakeX/Y (их считает UI.render ДО pixi — общий кадр).
   ===================================================================== */

import { Application, Container, Sprite, Texture, Graphics, Mesh, Geometry, Shader } from 'pixi.js';
import { Game } from './game';
import { CONFIG } from './config';
import { Render } from './render';
import { TAU, clamp, dist } from './utils';

// ---------- текстуры из offscreen-canvas (переиспользуем генерацию render.ts) ----------
const texCache = new Map<string, Texture>();
function glowDotTex(color: string, radius: number): Texture {
  radius = Math.max(1, Math.round(radius));
  const key = 'dot|' + color + '|' + radius;
  let t = texCache.get(key);
  if (t) return t;
  t = Texture.from(Render.glowDot(color, radius));
  texCache.set(key, t);
  return t;
}
function glowShapeTex(color: string, radius: number, shape: string): Texture {
  radius = Math.max(1, Math.round(radius));
  const key = 'shape|' + color + '|' + radius + '|' + shape;
  let t = texCache.get(key);
  if (t) return t;
  t = Texture.from(Render.glowShape(color, radius, shape));
  texCache.set(key, t);
  return t;
}
// кристалл-самоцвет опыта (HG-4): чёткая огранённая форма вместо мягкой точки → отделяет слой ОПЫТА от
// мягких blob-врагов и эффектов. Огранка через ЯРКОСТЬ (не темноту) — работает в additive-слое искр.
function gemTex(color: string, size: number): Texture {
  size = Math.max(2, Math.round(size));
  const key = 'gem|' + color + '|' + size;
  let t = texCache.get(key);
  if (t) return t;
  const R = size * 1.6, pad = Math.ceil(size * 1.1), s = Math.ceil(R * 2 + pad * 2), cx = s / 2;
  const c = document.createElement('canvas'); c.width = c.height = s;
  const g = c.getContext('2d')!;
  // мягкий внешний ореол энергии
  const grd = g.createRadialGradient(cx, cx, 0, cx, cx, R + pad);
  grd.addColorStop(0, color + 'aa'); grd.addColorStop(0.5, color + '44'); grd.addColorStop(1, color + '00');
  g.fillStyle = grd; g.fillRect(0, 0, s, s);
  // чёткое тело-ромб (огранённый кристалл)
  g.beginPath(); g.moveTo(cx, cx - R); g.lineTo(cx + R * 0.68, cx); g.lineTo(cx, cx + R); g.lineTo(cx - R * 0.68, cx); g.closePath();
  g.fillStyle = color; g.fill();
  // верхняя фасетка ярче — огранка читается яркостью (additive-safe)
  g.beginPath(); g.moveTo(cx, cx - R); g.lineTo(cx + R * 0.68, cx); g.lineTo(cx - R * 0.68, cx); g.closePath();
  g.fillStyle = 'rgba(255,250,235,0.5)'; g.fill();
  // белый блик-сердце
  g.beginPath(); g.arc(cx, cx - R * 0.15, Math.max(1, R * 0.17), 0, TAU); g.fillStyle = '#fffaf0'; g.fill();
  t = Texture.from(c);
  texCache.set(key, t);
  return t;
}
// запечённый радиальный градиент (для пятен тьмы и зон фонаря) по списку стопов
function radialTex(key: string, stops: [number, string][]): Texture {
  const k = 'radial|' + key;
  let t = texCache.get(k);
  if (t) return t;
  const size = 128, c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const cx = size / 2;
  const grd = g.createRadialGradient(cx, cx, 0, cx, cx, cx);
  for (const [o, col] of stops) grd.addColorStop(o, col);
  g.fillStyle = grd;
  g.beginPath(); g.arc(cx, cx, cx, 0, TAU); g.fill();
  t = Texture.from(c);
  texCache.set(k, t);
  return t;
}
function darkZoneTex() {
  // пятна Якорей — провалы остывшего холода (в палитре выгоревшего «внешнего»)
  return radialTex('darkzone', [[0, 'rgba(8,8,18,0.78)'], [0.7, 'rgba(8,8,18,0.44)'], [1, 'rgba(8,8,18,0)']]);
}
function lanternTex() {
  return radialTex('lantern', [[0, 'rgba(255,224,150,0.20)'], [0.6, 'rgba(255,206,94,0.07)'], [1, 'rgba(255,176,80,0)']]);
}

// ---------- пул спрайтов в контейнере (покадровая реконсиляция) ----------
class SpritePool {
  container: Container;
  sprites: Sprite[] = [];
  idx = 0;
  constructor(container: Container) { this.container = container; }
  begin() { this.idx = 0; }
  next(tex: Texture): Sprite {
    let s = this.sprites[this.idx];
    if (!s) {
      s = new Sprite(tex); s.anchor.set(0.5);
      this.container.addChild(s); this.sprites.push(s);
    } else { s.texture = tex; }
    s.visible = true; s.alpha = 1; s.rotation = 0; s.scale.set(1); s.tint = 0xffffff;
    this.idx++;
    return s;
  }
  end() { for (let i = this.idx; i < this.sprites.length; i++) this.sprites[i].visible = false; }
}

// ---------- fullscreen clip-space шейдеры (тьма / ореол) ----------
// big-triangle, покрывающий экран; vUV в 0..1 (y-вниз как у canvas — см. flip)
const VERT = `
in vec2 aPosition;
out vec2 vUV;
void main(){
  vUV = vec2((aPosition.x + 1.0) * 0.5, (1.0 - aPosition.y) * 0.5);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

// Работаем в UV-долях (0..1) с аспект-коррекцией — независимо от DPR/разрешения буфера.
// uCenter — UV (0.5±shake), uRadius — доля высоты экрана (light/H), uAspect — W/H.
// distV возвращает расстояние в «долях высоты», радиусы в тех же единицах.
const COMMON = `
float seg(float t, float t0, float t1, float a0, float a1){
  return mix(a0, a1, clamp((t - t0) / (t1 - t0), 0.0, 1.0));
}
float distV(vec2 uv, vec2 c, float aspect){
  vec2 q = (uv - c) * vec2(aspect, 1.0);
  return length(q);
}`;

// alpha-over оверлей: тьма игрока (радиал) + cycle-тинт + виньетка по HP. Выход премультиплен.
const DARK_FRAG = `
in vec2 vUV;
out vec4 finalColor;
uniform vec2 uCenter;
uniform float uRadius;
uniform float uAspect;
uniform float uTime;
uniform float uHpFrac;
uniform float uCycle;
${COMMON}
vec4 over(vec4 dst, vec3 c, float a){            // dst премультиплен, (c,a) straight
  return vec4(c * a + dst.rgb * (1.0 - a), a + dst.a * (1.0 - a));
}
void main(){
  vec4 acc = vec4(0.0);

  // основная тьма (радиал вокруг игрока): ri=r*0.62 .. ro=r*1.28
  float d = distV(vUV, uCenter, uAspect);
  float r = uRadius;
  float t = clamp((d - r * 0.62) / (r * 1.28 - r * 0.62), 0.0, 1.0);
  float aDark;
  if (t <= 0.55) aDark = seg(t, 0.0, 0.55, 0.0, 0.45);
  else if (t <= 0.85) aDark = seg(t, 0.55, 0.85, 0.45, 0.86);
  else aDark = seg(t, 0.85, 1.0, 0.86, 0.975);
  // «внешнее» — выгоревший остывший уголь (#1A1A2E→#0D0D1A), НЕ чёрный: у мира низкая температура (§10.1)
  vec3 darkCol = mix(vec3(26.0,26.0,46.0), vec3(13.0,13.0,26.0), t) / 255.0;
  acc = over(acc, darkCol, aDark);

  // cycle-тинт (плоский)
  if (abs(uCycle) >= 0.06){
    if (uCycle < 0.0) acc = over(acc, vec3(24.0,34.0,84.0)/255.0, 0.11 * (-uCycle));
    else acc = over(acc, vec3(255.0,176.0,80.0)/255.0, 0.05 * uCycle);
  }

  // виньетка по HP (экранно-центрированная: H*0.3 .. H*0.75 → доли высоты 0.3..0.75)
  if (uHpFrac < 0.35){
    float inten = (0.35 - uHpFrac) / 0.35;
    float pulse = 0.5 + 0.5 * sin(uTime * 6.0);
    float vd = distV(vUV, vec2(0.5, 0.5), uAspect);
    float vt = clamp((vd - 0.3) / 0.45, 0.0, 1.0);
    acc = over(acc, vec3(192.0,57.0,43.0)/255.0, 0.3 * inten * (0.6 + 0.4 * pulse) * vt);
  }

  finalColor = acc;
}`;

// аддитивный оверлей: жаркий ореол на кромке (янтарь→кармин) + «нервный» наклон к угрозе +
// карминовый пульс угрозы. uThreatDir — единичный вектор-направление давления, |len| = сила наклона. Выход — премультипл. add.
const GLOW_FRAG = `
in vec2 vUV;
out vec4 finalColor;
uniform vec2 uCenter;
uniform float uRadius;
uniform float uAspect;
uniform float uThreatA;
uniform vec2 uThreatDir;
uniform float uTime;
${COMMON}
void main(){
  vec2 q = (vUV - uCenter) * vec2(uAspect, 1.0);
  float d = length(q);
  float r = uRadius;
  vec3 add = vec3(0.0);

  // «нервный» ореол (§8.3): ярче в сторону давления врагов — пламя «смотрит» на угрозу
  vec2 nd = q / max(d, 1e-4);
  float lean = 1.0 + dot(nd, uThreatDir) * 0.95;

  // жаркий ореол кромки: hi=r*0.86 .. ho=r*1.05; цвет лерпит янтарь(внутри)→кармин(на самой границе)
  float ht = clamp((d - r * 0.86) / (r * 1.05 - r * 0.86), 0.0, 1.0);
  float ha = (ht <= 0.7) ? seg(ht, 0.0, 0.7, 0.0, 0.055) : seg(ht, 0.7, 1.0, 0.055, 0.0);
  // ПЛАМЕННАЯ кромка: угловая модуляция языками вместо идеального кольца (убирает последний геом-маркер)
  float ang = atan(q.y, q.x);
  float lick = 0.72 + 0.28 * sin(ang * 6.0 + uTime * 2.3) + 0.12 * sin(ang * 11.0 - uTime * 1.6);
  ha *= lean * lick;
  vec3 haloCol = mix(vec3(255.0,160.0,55.0), vec3(192.0,57.0,43.0), ht) / 255.0;
  add += haloCol * ha;

  // пульс угрозы: карминовая полоса r*0.92 .. r*1.5, треугольный пик в центре
  if (uThreatA > 0.0){
    float pt = clamp((d - r * 0.92) / (r * 1.5 - r * 0.92), 0.0, 1.0);
    float band = clamp(1.0 - abs(pt - 0.5) / 0.5, 0.0, 1.0);
    add += vec3(192.0,57.0,43.0)/255.0 * (uThreatA * band);
  }

  finalColor = vec4(add, max(ha, uThreatA));
}`;

// полноэкранный пост: плёночное зерно (дробит «чистый вектор») + тёплая пепельная дымка-виньетка по краям.
// alpha-over поверх всей сцены, выход премультиплен (как DARK_FRAG).
const GRAIN_FRAG = `
in vec2 vUV;
out vec4 finalColor;
uniform float uTime;
uniform vec2 uRes;
vec4 over(vec4 dst, vec3 c, float a){ return vec4(c * a + dst.rgb * (1.0 - a), a + dst.a * (1.0 - a)); }
float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
void main(){
  vec4 acc = vec4(0.0);
  // тёплая пепельная дымка к краям кадра (мир сгущается по периметру, не чёрная рамка)
  vec2 d = vUV - 0.5;
  float vig = smoothstep(0.34, 0.92, length(d) * 1.18);
  acc = over(acc, vec3(28.0, 18.0, 14.0) / 255.0, vig * 0.55);
  // плёночное зерно — анимированный шум, мягко (текстура, не снег)
  float g = hash(vUV * uRes + fract(uTime * 0.7) * 311.0);
  acc = over(acc, vec3(0.5 + (g - 0.5) * 0.85), 0.05);
  finalColor = acc;
}`;

function makeFsMesh(frag: string, uniforms: any): Mesh {
  const geometry = new Geometry({ attributes: { aPosition: [-1, -1, 3, -1, -1, 3] } });
  const shader = Shader.from({ gl: { vertex: VERT, fragment: frag }, resources: { fsUniforms: uniforms } });
  const mesh = new Mesh({ geometry, shader } as any);
  return mesh;
}

export const PixiRenderer: any = {
  app: null,
  ready: false,
  W: 0, H: 0,

  async init(canvasEl: HTMLCanvasElement, w: number, h: number, dpr: number) {
    this.app = new Application();
    await this.app.init({
      canvas: canvasEl, width: w, height: h,
      background: CONFIG.colors.bg, backgroundAlpha: 1,
      antialias: true, resolution: dpr, autoDensity: true,
      preference: 'webgl', autoStart: false,
    });
    this.W = w; this.H = h;

    // ---- сцена ----
    const stage = this.app.stage;
    this.worldRoot = new Container();
    this.gridGfx = new Graphics();
    this.shapes = new Container();
    this.additive = new Container(); (this.additive as any).blendMode = 'add';
    this.effectsGfx = new Graphics(); (this.effectsGfx as any).blendMode = 'add';
    this.darkZones = new Container();
    this.silGfx = new Graphics();   // чёткий контур-якорь игрока: поверх толпы, под fullscreen-тьмой (HG-2)
    this.additive.addChild(this.effectsGfx);
    this.worldRoot.addChild(this.gridGfx, this.shapes, this.additive, this.darkZones, this.silGfx);

    this.glints = new Container(); (this.glints as any).blendMode = 'add';

    // ---- пулы ----
    this.poolShapes = new SpritePool(this.shapes);          // враги + силуэт игрока
    this.poolAdd = new SpritePool(this.additive);           // искры, снаряды, частицы, орбита, ядро, фонарь
    this.poolZones = new SpritePool(this.darkZones);        // пятна Якорей
    this.poolGlints = new SpritePool(this.glints);          // глинты
    this.zoneRings = new Graphics();
    this.darkZones.addChild(this.zoneRings);

    // ---- fullscreen меши тьмы/ореола (юниформы в UV-долях + аспект) ----
    this.darkU = { uCenter: { value: new Float32Array([0.5, 0.5]), type: 'vec2<f32>' },
                   uRadius: { value: 0.3, type: 'f32' },
                   uAspect: { value: w / h, type: 'f32' },
                   uTime: { value: 0, type: 'f32' },
                   uHpFrac: { value: 1, type: 'f32' },
                   uCycle: { value: 0, type: 'f32' } };
    this.gloU = { uCenter: { value: new Float32Array([0.5, 0.5]), type: 'vec2<f32>' },
                  uRadius: { value: 0.3, type: 'f32' },
                  uAspect: { value: w / h, type: 'f32' },
                  uThreatA: { value: 0, type: 'f32' },
                  uThreatDir: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
                  uTime: { value: 0, type: 'f32' } };
    this.darkMesh = makeFsMesh(DARK_FRAG, this.darkU);
    this.gloMesh = makeFsMesh(GLOW_FRAG, this.gloU); (this.gloMesh as any).blendMode = 'add';

    // амбиентное поле пепла/угольков — материя в воздухе (поверх тьмы, под ореолом)
    this.ambient = new Container(); (this.ambient as any).blendMode = 'add';
    this.poolAmbient = new SpritePool(this.ambient);
    this.ashMotes = [];

    // полноэкранное зерно + дымка — поверх ВСЕГО
    this.grainU = { uTime: { value: 0, type: 'f32' },
                    uRes: { value: new Float32Array([w, h]), type: 'vec2<f32>' } };
    this.grainMesh = makeFsMesh(GRAIN_FRAG, this.grainU);

    stage.addChild(this.worldRoot, this.darkMesh, this.ambient, this.gloMesh, this.glints, this.grainMesh);
    this.ready = true;
  },

  resize(w: number, h: number) {
    if (!this.ready) return;
    this.W = w; this.H = h;
    this.app.renderer.resize(w, h);
    this.darkU.uAspect.value = w / h;
    this.gloU.uAspect.value = w / h;
    this.grainU.uRes.value[0] = w; this.grainU.uRes.value[1] = h;
  },

  // ---- кадр ----
  render() {
    if (!this.ready) return;
    const st = Game.state;
    const showWorld = st === 'playing' || st === 'paused' || st === 'levelup' || st === 'shrine' || st === 'gameover' || st === 'win' || st === 'speak';
    this.worldRoot.visible = showWorld;
    this.darkMesh.visible = showWorld;
    this.ambient.visible = showWorld;
    this.gloMesh.visible = showWorld;
    this.glints.visible = showWorld;
    this.grainU.uTime.value = Game.clock || 0;   // зерно живёт всегда (в т.ч. на меню/паузе)
    if (showWorld) this.drawWorld();
    this.app.render();
  },

  drawWorld() {
    const p = Game.player, W = this.W, H = this.H, t = Game.time;
    // общий покадровый dt (real-time, тикает и во фризе) — для дрейфа пепла и угольков
    const now = Game.clock || 0;
    let fdt = now - (this._frameLast != null ? this._frameLast : now); this._frameLast = now;
    if (fdt < 0 || fdt > 0.05) fdt = 0.016;
    this._frameDt = fdt;
    const camX = W / 2 - p.x + Game.shakeX, camY = H / 2 - p.y + Game.shakeY;
    this.worldRoot.position.set(camX, camY);
    this.glints.position.set(camX, camY);

    this.drawGrid(p, W, H);
    this.drawAmbient(p, W, H);

    this.poolShapes.begin(); this.poolAdd.begin(); this.poolZones.begin(); this.poolGlints.begin();
    this.effectsGfx.clear(); this.zoneRings.clear(); this.silGfx.clear();

    // ---- искры (additive) ---- (осколок опыта тускнеет к концу жизни — остывает)
    for (const k of Game.pickups.active) {
      if (k.dead) continue;
      const lf = k.life > 0 ? clamp(k.life / 3, 0.12, 1) : 1;   // последние ~3с гаснет
      if (k.type === 'xp') {
        const fl = 0.7 + 0.3 * Math.sin(t * 9 + k.born * 7);
        const s = this.poolAdd.next(gemTex(k.color, 3)); s.position.set(k.x, k.y);
        s.rotation = t * 1.5 + k.born * 5;   // лёгкое вращение — грань ловит свет, читается как самоцвет
        s.scale.set(0.85 + 0.25 * fl); s.alpha = (0.85 * fl + 0.12) * lf;
      } else {
        const s = this.poolAdd.next(gemTex(k.color, k.type === 'xpbig' ? 7 : 6));
        s.position.set(k.x, k.y); s.rotation = t * 1.2 + k.born * 4; s.alpha = 0.97 * lf;
      }
    }

    // ---- nova-эффекты (additive, под врагами) ----
    for (const e of Game.effects) {
      if (e.kind !== 'nova') continue;
      const a = clamp(e.life / e.maxLife, 0, 1);
      this.effectsGfx.circle(e.x, e.y, e.r).stroke({ width: 3 + a * 7, color: colorNum(e.color), alpha: a * 0.8 });
    }

    // ВЫЖЖЕННАЯ ЗЕМЛЯ — визуал жар-свечения территории УБРАН по запросу (остался лишь маленький
    // декоративный шлейф за игроком, см. ниже). Сама механика (тление-DoT/замедление/синергия D2)
    // продолжает работать невидимо — метки emberField живут в game.ts.

    // ---- враги (shapes) + флеш (additive) ----
    for (const e of Game.enemies.active) {
      if (e.dead) continue;
      const X = e.x - p.x + W / 2, Y = e.y - p.y + H / 2;   // экранная отбраковка
      if (X < -60 || X > W + 60 || Y < -60 || Y > H + 60) continue;
      // ВИДНО ТОЛЬКО В СВЕТЕ: в зоне света враг виден полностью, у границы плавно гаснет, в настоящей
      // тьме не рисуем НИ тело, НИ контур — там врага выхватывают лишь редкие глинты-вспышки (drawGlints).
      const dToP = dist(e.x, e.y, p.x, p.y);
      let lit = 1;
      if (dToP > p.light) { lit = 1 - (dToP - p.light) / (p.light * 0.16); if (lit <= 0.02) continue; }
      const s = this.poolShapes.next(glowShapeTex(e.color, e.radius, e.shape));
      s.position.set(e.x, e.y); s.alpha = lit;
      // враги «корчатся» как горящие/тающие формы — нерегулярный jitter по осям + вихляние (e.wob = фаза);
      // боссы массивнее → колышутся слабее. Амплитуда умеренная — силуэт-архетип остаётся читаем (пивот #1).
      const jw = e.isBoss ? 0.5 : 1;
      const jex = 1 + Math.sin(t * 6 + e.wob) * 0.12 * jw + Math.sin(t * 2.3 + e.wob * 2) * 0.06 * jw;
      const jey = 1 + Math.sin(t * 5.2 + e.wob * 1.5 + 1.1) * 0.14 * jw + Math.sin(t * 3.1 + e.wob) * 0.05 * jw;
      s.scale.set(jex, jey);
      s.rotation = (e.isBoss ? t * 0.4 : (e.wob + t * 0.8)) + Math.sin(t * 7 + e.wob) * 0.05 * jw;
      // ЧЁТКИЙ КОНТУР-СИЛУЭТ ВРАГА (HG-3): тёмный gap разделяет силуэты в плотной толпе + дифференциация типа формой.
      // без светлого rim (150 врагов = суп и конкуренция с золотым якорем игрока — правка менеджера). blob остаётся как тело.
      const eg = this.silGfx, erot = s.rotation, ecol = colorNum(e.color);
      if (e.shape === 'block' || e.shape === 'vortex') {   // массивным/боссам — приглушённая заливка для «веса»
        if (tracePoly(eg, e.x, e.y, shapePolys(e.shape, e.radius), erot, jex, jey)) eg.fill({ color: ecol, alpha: 0.18 * lit });
      }
      // тончайший тёмный gap снаружи — держит силуэты раздельными в плотной толпе пика (HG-3);
      // под блумом почти незаметен, но не даёт кантам слиться в кашу на 150 врагах
      if (tracePoly(eg, e.x, e.y, shapePolys(e.shape, e.radius * 1.16), erot, jex, jey)) eg.stroke({ width: 1.0, color: 0x05030a, alpha: 0.5 * lit });
      else { eg.circle(e.x, e.y, e.radius * 1.14); eg.stroke({ width: 1.0, color: 0x05030a, alpha: 0.5 * lit }); }
      // БЛУМ-ОБВОДКА врага (как у игрока, но бело-ХОЛОДНАЯ): мягкий внешний ореол свечения + тонкая чёткая линия.
      // боссы остаются тёплыми (красная угроза против холодного роя), рядовые — морозный бело-цветной кант.
      const rimC = e.isBoss ? rimColor(ecol) : coolRim(ecol);
      // 1) мягкий внешний блум — широкий полупрозрачный холодный ореол канта
      if (tracePoly(eg, e.x, e.y, shapePolys(e.shape, e.radius * 1.1), erot, jex, jey)) eg.stroke({ width: 2.0, color: rimC, alpha: 0.20 * lit });
      else { eg.circle(e.x, e.y, e.radius * 1.08); eg.stroke({ width: 2.0, color: rimC, alpha: 0.20 * lit }); }
      // 2) тонкая чёткая бело-холодная окантовка по телу
      if (tracePoly(eg, e.x, e.y, shapePolys(e.shape, e.radius * 1.0), erot, jex, jey)) eg.stroke({ width: 1.1, color: rimC, alpha: 0.9 * lit });
      else { eg.circle(e.x, e.y, e.radius); eg.stroke({ width: 1.1, color: rimC, alpha: 0.9 * lit }); }
      // ФРОНТ УГРОЗЫ (HG-31, visual-only): враг вплотную к игроку → красный тревожный пульс-контур «опасность сейчас».
      // только визуальный сигнал по позиции — поведение/агро/группировку врагов НЕ трогаем.
      const dpx = e.x - p.x, dpy = e.y - p.y, near2 = 64 * 64;
      if (!e.isBoss && dpx * dpx + dpy * dpy < near2) {
        const pz = 0.55 + 0.45 * Math.sin(t * 9 + e.wob);
        if (tracePoly(eg, e.x, e.y, shapePolys(e.shape, e.radius * 1.24), erot, jex, jey)) eg.stroke({ width: 2, color: 0xff3b1e, alpha: 0.5 * pz * lit });
        else { eg.circle(e.x, e.y, e.radius * 1.22); eg.stroke({ width: 2, color: 0xff3b1e, alpha: 0.5 * pz * lit }); }
      }
      if (e.flash > 0) {
        const fa = clamp(e.flash / CONFIG.feel.hitFlash, 0, 1) * lit;   // во тьме враг не вспыхивает контуром
        const f = this.poolAdd.next(glowDotTex('#ffffff', e.radius * 0.9));
        f.position.set(e.x, e.y); f.alpha = fa * 0.8;
        // directional брызги удара (HG-7): искры летят ОТ игрока сквозь врага — «удар пришёл отсюда», не радиальная вспышка
        const ha = Math.atan2(e.y - p.y, e.x - p.x), hx = Math.cos(ha), hy = Math.sin(ha);
        for (let si = 0; si < 2; si++) {
          const sd = e.radius * 0.6 + si * 4;
          const sp = this.poolAdd.next(glowDotTex('#fff3d0', 2));
          sp.position.set(e.x + hx * sd, e.y + hy * sd); sp.alpha = fa * 0.55;
        }
      }
    }

    // ---- снаряды (additive) ----
    for (const pr of Game.projectiles.active) {
      if (pr.dead) continue;
      if (pr.kind === 'bolt') {
        // огненная комета: затухающий хвост позади + вытянутое тело-искра
        const ang = Math.atan2(pr.vy, pr.vx), bx = Math.cos(ang), by = Math.sin(ang);
        for (let ti = 1; ti <= 2; ti++) {
          const tl = this.poolAdd.next(glowDotTex(pr.color, pr.radius * (1 - ti * 0.25)));
          tl.position.set(pr.x - bx * ti * pr.radius * 1.8, pr.y - by * ti * pr.radius * 1.8);
          tl.alpha = 0.5 / ti;
        }
        const s = this.poolAdd.next(glowDotTex(pr.color, pr.radius));
        s.position.set(pr.x, pr.y); s.rotation = ang; s.scale.set(1.9, 0.7);
      } else if (pr.kind === 'mine') {
        const s = this.poolAdd.next(glowDotTex(pr.color, 5));
        s.position.set(pr.x, pr.y); s.scale.set(1 + Math.sin(Game.clock * 8) * 0.15); s.alpha = 0.95;
        this.effectsGfx.circle(pr.x, pr.y, pr.trigger).stroke({ width: 1, color: 0xffd27a, alpha: 0.14 });
      } else if (pr.kind === 'lantern') {
        const a = clamp(pr.life / (pr.maxLife || 1), 0, 1);
        const z = this.poolAdd.next(lanternTex());
        z.position.set(pr.x, pr.y); z.scale.set(pr.radius / 64); z.alpha = a;
        const core = this.poolAdd.next(glowDotTex(pr.color, 6));
        core.position.set(pr.x, pr.y); core.scale.set(1 + Math.sin(Game.clock * 6) * 0.2); core.alpha = a;
      } else {
        // базовый снаряд — вектор-комета (HG-7): хвост + вытянутое по движению тело, читается «откуда-куда»,
        // в отличие от радиальных аур (nova/heat) — игрок отличает СВОЙ удар от пассивного эффекта.
        const ang = Math.atan2(pr.vy, pr.vx), bx = Math.cos(ang), by = Math.sin(ang);
        for (let ti = 1; ti <= 2; ti++) {
          const tl = this.poolAdd.next(glowDotTex(pr.color, pr.radius * (1 - ti * 0.28)));
          tl.position.set(pr.x - bx * ti * pr.radius * 1.6, pr.y - by * ti * pr.radius * 1.6);
          tl.alpha = 0.45 / ti;
        }
        const s = this.poolAdd.next(glowDotTex(pr.color, pr.radius));
        s.position.set(pr.x, pr.y); s.rotation = ang; s.scale.set(1.7, 0.78);
      }
    }

    // ---- цепь-молния, хлыст (additive Graphics) ----
    for (const e of Game.effects) {
      if (e.kind === 'chain') {
        const a = clamp(e.life / e.maxLife, 0, 1);
        const g = this.effectsGfx; g.moveTo(e.pts[0].x, e.pts[0].y);
        for (let i = 1; i < e.pts.length; i++) g.lineTo(e.pts[i].x, e.pts[i].y);
        g.stroke({ width: 2.5, color: colorNum(e.color), alpha: a });
      } else if (e.kind === 'whip') {
        // серп-росчерк: тело-заливка + резкая яркая кромка + вспышка на кончике (визуал сочнее)
        const a = clamp(e.life / e.maxLife, 0, 1);
        const dx = Math.cos(e.ang), dy = Math.sin(e.ang), nx = -dy, ny = dx;
        const bx = e.x, by = e.y;
        const p1x = bx + nx * e.wide * 0.3, p1y = by + ny * e.wide * 0.3;
        const cx1 = bx + dx * e.len * 0.6 - nx * e.wide, cy1 = by + dy * e.len * 0.6 - ny * e.wide;
        const ex = bx + dx * e.len, ey = by + dy * e.len;
        const cx2 = bx + dx * e.len * 0.6 + nx * e.wide, cy2 = by + dy * e.len * 0.6 + ny * e.wide;
        const p2x = bx - nx * e.wide * 0.3, p2y = by - ny * e.wide * 0.3;
        const g = this.effectsGfx;
        g.moveTo(p1x, p1y); g.quadraticCurveTo(cx1, cy1, ex, ey); g.quadraticCurveTo(cx2, cy2, p2x, p2y); g.closePath();
        g.fill({ color: colorNum(e.color), alpha: a * 0.4 });
        // яркая передняя кромка серпа — резкий росчерк
        g.moveTo(p1x, p1y); g.quadraticCurveTo(cx1, cy1, ex, ey);
        g.stroke({ width: 3, color: colorNum(e.color), alpha: a * 0.95 });
        // белёсая вспышка на кончике
        const tip = this.poolAdd.next(glowDotTex('#fffdf0', 7));
        tip.position.set(bx + dx * e.len * 0.92, by + dy * e.len * 0.92); tip.alpha = a * 0.85;
      }
    }

    // ---- орбита (additive спрайты) и луч-маяк (Graphics) ----
    for (const w of p.weapons) {
      const def = wdef(w.key);
      if (w._nodes && def && def.kind === 'orbit') {
        const tex = glowShapeTex(def.color, def.radius, 'circle');
        for (let i = 0; i < w._nodeCount; i++) {
          const nd = w._nodes[i];
          const s = this.poolAdd.next(tex); s.position.set(nd.x, nd.y); s.rotation = t * 3; s.alpha = 0.95;
        }
      }
      if (w._beams && def && def.kind === 'beam') {
        const hw = def.beamWide * 0.5;
        const al = 0.45 + Math.sin(Game.clock * 18) * 0.1;
        for (const b of w._beams) {
          const dx = Math.cos(b.ang), dy = Math.sin(b.ang), nx = -dy, ny = dx;
          const ax = p.x, ay = p.y, ex = p.x + dx * b.len, ey = p.y + dy * b.len;
          const g = this.effectsGfx;
          g.moveTo(ax + nx * hw, ay + ny * hw).lineTo(ex + nx * hw, ey + ny * hw)
           .lineTo(ex - nx * hw, ey - ny * hw).lineTo(ax - nx * hw, ay - ny * hw).closePath()
           .fill({ color: colorNum(def.color), alpha: al });
          g.moveTo(ax + nx * 2, ay + ny * 2).lineTo(ex + nx * 2, ey + ny * 2)
           .lineTo(ex - nx * 2, ey - ny * 2).lineTo(ax - nx * 2, ay - ny * 2).closePath()
           .fill({ color: 0xfffdf0, alpha: 0.7 });
        }
      }
    }

    // ---- аура Инея (зона замедления) + Ореол гнева (зона жжения = радиус света) + ореол бафа ----
    // ГРАНИЦА ЖАРА — «мой радиус» всегда читаем (HG-5): чёткое кольцо по p.light на silGfx (поверх толпы,
    // не тонет на пике). Цвет/яркость дышат со свет-осью: багровеет на просадке, бело-жёлтый жар на пике —
    // «я ярче / я меньше» наблюдаемо. Тёмный внешний кант вырезает «мою территорию» из внешнего хаоса.
    {
      const lf = clamp((p.light - CONFIG.light.min) / (CONFIG.light.base - CONFIG.light.min), 0, 1);
      const sg = this.silGfx, R = p.light, pulse = 0.5 + 0.5 * Math.sin(t * 2.5);
      const edgeC = coreTempTint(0.15 + 0.85 * lf);
      sg.circle(p.x, p.y, R + 3).stroke({ width: 5, color: 0x120802, alpha: 0.55 });            // тёмный кант — вырезает территорию из хаоса
      sg.circle(p.x, p.y, R).stroke({ width: 3, color: edgeC, alpha: 0.55 + 0.28 * pulse });     // яркая граница жара
      this.effectsGfx.circle(p.x, p.y, R - 1).stroke({ width: 2, color: colorNum('#ff8a36'), alpha: (0.16 + 0.08 * pulse) }); // тёплый внутр. жар-блик (additive)
    }
    if (p.frostR > 0) {
      this.effectsGfx.circle(p.x, p.y, p.frostR)
        .stroke({ width: 2, color: 0x9fe8ff, alpha: 0.10 + 0.04 * Math.sin(t * 2) });
    }
    if (p._auraThorns) {
      // реликвия «Венец гнева»: свет жжёт тьму в радиусе света — тёплый пурпур, пульс жжения
      this.effectsGfx.circle(p.x, p.y, p.light)
        .stroke({ width: 2, color: 0xff7ad6, alpha: 0.08 + 0.05 * Math.sin(t * 4) });
    }
    if (Game.bless) {
      const ba = clamp(Game.bless.life / Game.bless.maxLife, 0, 1);
      const halo = this.poolAdd.next(glowDotTex('#ffd27a', 26));
      halo.position.set(p.x, p.y); halo.scale.set(1 + 0.15 * Math.sin(t * 5));
      halo.alpha = 0.4 * (0.55 + 0.45 * ba);   // тёплый «силовой» ореол на время бафа
    }

    // ---- игрок (силуэт-пламя + ядро) ----
    const ch = CONFIG.characters[p.charKey] || CONFIG.characters.spark;
    let pAlpha = 1;
    if (p.invuln > 0) pAlpha = (Math.floor(t * 20) % 2) ? 0.35 : 1;
    // «пинок» от удара: пламя резко сжимается и спружинивает (§8.3 — боль без анимации)
    const kick = p._coreKick || 0;
    // РАЗМЕР ГЕРОЯ = ЗАПАС HP (100 HP = 100% размера): фигура растёт с прокачкой maxHp и съёживается
    // при ранении — по размеру читается запас здоровья. Сглажено (×0.6) и ограничено [0.62..1.6],
    // чтобы не делать гиганта/точку. Влияет только на ВИЗУАЛ (хитбокс p.radius не трогаем — баланс контакта).
    const hpScale = clamp(1 + (p.hp / 100 - 1) * 0.6, 0.62, 1.6);
    // ПЛАВНЫЙ РАЗВОРОТ ФИГУРЫ: нос едет к направлению движения по короткой дуге, а не щёлкает мгновенно.
    // храним сглаженный угол на рендерере и догоняем цель (~0.1с) — анимированный поворот при ходьбе.
    const targetAng = Math.atan2(p.lastDir.y, p.lastDir.x) + Math.PI / 2;
    if (this._silAng === undefined) this._silAng = targetAng;
    let dAng = targetAng - this._silAng;
    while (dAng > Math.PI) dAng -= TAU;
    while (dAng < -Math.PI) dAng += TAU;
    this._silAng += dAng * Math.min(1, fdt * 12);
    const faceAng = this._silAng;
    const sil = this.poolShapes.next(glowShapeTex(ch.color || CONFIG.colors.player, p.radius, ch.shape || 'diamond'));
    sil.position.set(p.x, p.y);
    // нерегулярное «дыхание» формы: разные фазы по осям + лёгкое вихляние — силуэт колышется как пламя, а не ровный ромб
    const ksc = 1 - 0.4 * kick;
    const jx = 1 + Math.sin(t * 8.3) * 0.11 + Math.sin(t * 3.1 + 1.3) * 0.06;
    const jy = 1 + Math.sin(t * 7.1 + 1.7) * 0.13 + Math.sin(t * 4.3) * 0.05;
    sil.rotation = faceAng + Math.sin(t * 9) * 0.06;
    sil.alpha = pAlpha; sil.scale.set(jx * ksc * hpScale, jy * ksc * hpScale);
    // ЧЁТКИЙ СИЛУЭТ-ЯКОРЬ (HG-2): контур формы героя поверх свечения — «вот герой», читается в супе пика.
    // blob выше = объём огня, этот контур = граница, ядро ниже = сердце (три слоя). Стабильнее врагов (он якорь).
    // заливка дышит со свет-осью (тускнеет/багровеет на просадке light), тёмный кант вырезает из хаоса, светлый rim — объём.
    {
      const g = this.silGfx;
      const lfNow = clamp((p.light - CONFIG.light.min) / (CONFIG.light.base - CONFIG.light.min), 0, 1);
      // КРУПНЕЕ рядового врага (×1.5) — герой-якорь должен доминировать в толпе, не тонуть (HG-8 находка)
      const R = p.radius * 1.5 * hpScale * (1 - 0.3 * kick) * (1 + 0.04 * Math.sin(t * 6));
      const rot = faceAng;                               // плавный нос по движению (сглаженный угол, см. выше)
      const cr = Math.cos(rot), sr = Math.sin(rot);
      const crad = R * 0.42;                             // радиус скругления углов окантовки — без острых вершин
      // путь силуэта со СКРУГЛЁННЫМИ углами (pixi roundShape): все углы закруглены, без острых
      const roundPathAt = (scale: number) => {
        const pp = shapePolys(ch.shape || 'diamond', R * scale);
        if (!pp) { g.circle(p.x, p.y, R * 0.92 * scale); return; }
        const pts: { x: number; y: number }[] = [];
        for (let i = 0; i < pp.length; i++) {
          pts.push({ x: p.x + (pp[i][0] * cr - pp[i][1] * sr), y: p.y + (pp[i][0] * sr + pp[i][1] * cr) });
        }
        g.roundShape(pts as any, crad * scale);
      };
      const baseC = colorNum(ch.color || CONFIG.colors.player);
      const fillC = lerpColor(0x3a1404, baseC, 0.34 + 0.66 * lfNow);   // багровеет на просадке света
      const bloomC = 0xfff2cc;                           // бело-жёлтый блум-кант (вместо чёрной обводки)
      // 1) мягкий внешний блум — широкий полупрозрачный бело-жёлтый ореол канта (свечение, а не чёрный gap)
      roundPathAt(1.07); g.stroke({ width: 2.2, color: bloomC, alpha: 0.20 * pAlpha });
      // 2) плотное тело — непрозрачное, перекрывает собственный glow/языки под ним → силуэт не тонет
      roundPathAt(1.0); g.fill({ color: fillC, alpha: 0.94 * pAlpha });
      // 3) тонкая чёткая бело-жёлтая окантовка — в ~2 раза тоньше прежней, скруглённая
      roundPathAt(1.0); g.stroke({ width: 1.1, color: bloomC, alpha: (0.55 + 0.4 * lfNow) * pAlpha });
    }
    // СИНЕРГИЯ D2: в ПЛОТНОЙ своей выжженной территории тёмный билд (_loEdge) «накаляется» —
    // тёплый пульсирующий ореол силы вокруг ядра. Легибельный сигнал «ты усилен здесь» (дискавери комбо).
    const efc = (CONFIG.emberField && CONFIG.emberField.synergyCap) || 3;
    const synAmt = (p._loEdge > 0 && (p._emberDensity || 0) > 1)
      ? Math.min((p._emberDensity || 0) - 1, efc) / efc : 0;
    if (synAmt > 0.01) {
      // широкий тёплый ореол силы + горячее кольцо-кромка: «накал» читается отдельно от пламени
      const aura = this.poolAdd.next(glowDotTex('#ff8c2e', p.radius * 3.4));
      aura.position.set(p.x, p.y);
      aura.alpha = (0.26 + 0.16 * Math.sin(t * 7)) * synAmt;
      aura.scale.set(1 + 0.16 * Math.sin(t * 5));
      const ring = this.poolAdd.next(glowDotTex('#ffd27a', p.radius * 1.8));
      ring.position.set(p.x, p.y);
      ring.alpha = (0.22 + 0.14 * Math.sin(t * 9 + 1)) * synAmt;
    }
    // СИНЕРГИЯ светлого полюса: на ПИКЕ маятника светлый билд (_hiEdge) «вспыхивает» — золотой
    // burst-сигнал (делает невидимое окно цикла видимым, дискавери ритм-комбо)
    if (p._hiEdge > 0) {
      const peakF = Math.max(0, (Game.dayNightMult() - 1) / (CONFIG.cycle.swing || 1));
      if (peakF > 0.35) {
        const k2 = (peakF - 0.35) / 0.65;
        const gl = this.poolAdd.next(glowDotTex('#ffe9a8', p.radius * 3.3));
        gl.position.set(p.x, p.y);
        gl.alpha = (0.22 + 0.16 * Math.sin(t * 8)) * k2;
        gl.scale.set(1 + 0.18 * Math.sin(t * 6));
      }
    }
    // живое пламя: 3 смещённых тёплых блоба мерцают вразнобой и тянутся вверх языками — нерегулярный огонь, не ровная точка (§8.3)
    const flameCol = ['#ff7a2a', '#ffae3a', '#ff5a1a'];
    for (let i = 0; i < 3; i++) {
      const ph = i * 2.1;
      const fx = p.x + Math.sin(t * (5 + i) + ph) * (3 + i * 1.5);
      const fy = p.y - Math.abs(Math.sin(t * (6 + i) + ph)) * (4 + i * 2) - i * 1.5;
      const fl = 0.6 + 0.4 * Math.sin(t * (7 + i * 1.3) + ph);
      const b = this.poolAdd.next(glowDotTex(flameCol[i], 7 + i * 2));
      b.position.set(fx, fy);
      b.alpha = (0.3 - i * 0.06) * pAlpha * (0.6 + 0.4 * fl);
      b.scale.set((0.7 + 0.5 * fl) * (1 - 0.35 * kick));
    }
    // температура ядра = «живость» пламени: при просадке света теряет голубой подсвет → желтеет → багровеет (§10.1)
    const lightFrac = clamp((p.light - CONFIG.light.min) / (CONFIG.light.base - CONFIG.light.min), 0, 1);
    const breathe = (5 + Math.sin(t * 6) * 1.2) / 5;
    const flash = p._coreFlash || 0;   // вспышка роста: ядро раздувается в ослепительный белый и опадает
    // ореол вспышки роста — кремово-белый разлёт жара вокруг ядра (§10.5 «уголёк→вспышка»):
    // широкий мягкий блум опадает наружу + тугое раскалённое ядро на пике — «взрыв в белый»
    if (flash > 0.01) {
      const fh = this.poolAdd.next(glowDotTex('#fff8d0', p.radius * 3.2));
      fh.position.set(p.x, p.y); fh.alpha = flash * 0.85; fh.scale.set(0.7 + (1 - flash) * 2.0);
      const hot = this.poolAdd.next(glowDotTex('#ffffff', p.radius * 1.5));
      hot.position.set(p.x, p.y); hot.alpha = flash * flash * 0.9; hot.scale.set(0.6 + flash * 0.6);
    }
    const core = this.poolAdd.next(glowDotTex(CONFIG.colors.playerCore, 5));
    core.position.set(p.x, p.y); core.scale.set((breathe + flash * 1.8) * (1 - 0.5 * kick) * hpScale); core.alpha = pAlpha;
    core.tint = flash > 0.02 ? 0xffffff : coreTempTint(lightFrac);   // на пике вспышки — чистый белый

    // всплывающие угольки от пламени — редкие нерегулярные искры вверх («ты горишь»), малый слот-пул
    if (!this.playerEmbers) this.playerEmbers = [];
    const emb = this.playerEmbers;
    if (Math.random() < 0.3 && emb.length < 8) {
      const life = 0.45 + Math.random() * 0.5;
      emb.push({ x: p.x + (Math.random() - 0.5) * p.radius * 1.4, y: p.y + (Math.random() - 0.5) * p.radius,
        vx: (Math.random() - 0.5) * 14, vy: -(28 + Math.random() * 40), life, max: life, size: 1.4 + Math.random() * 1.8 });
    }
    for (let i = emb.length - 1; i >= 0; i--) {
      const e2 = emb[i];
      e2.life -= fdt; if (e2.life <= 0) { emb.splice(i, 1); continue; }
      e2.vy -= 30 * fdt; e2.x += e2.vx * fdt; e2.y += e2.vy * fdt;   // тянутся вверх
      const f = e2.life / e2.max;
      const es = this.poolAdd.next(glowDotTex('#ff8a3a', 3));
      es.position.set(e2.x, e2.y); es.alpha = 0.6 * f * pAlpha; es.scale.set((e2.size / 2.2) * (0.5 + 0.5 * f));
    }

    // ДЕКОРАТИВНЫЙ ШЛЕЙФ (только косметика, быстро гаснет ~0.45с): редкие тёплые угольки оседают
    // по пути движения и тут же тают. НЕ геймплей — «выжженная земля» (тление/синергия) живёт в game.ts.
    if (!this.playerTrail) { this.playerTrail = []; this._trailLast = { x: p.x, y: p.y }; }
    const ptr = this.playerTrail, tl = this._trailLast;
    const tdx = p.x - tl.x, tdy = p.y - tl.y;
    if (tdx * tdx + tdy * tdy > 18 * 18 && ptr.length < 12) {   // оседает по мере движения, не при стоянии
      ptr.push({ x: p.x, y: p.y, life: 0.45, max: 0.45 });
      tl.x = p.x; tl.y = p.y;
    }
    for (let i = ptr.length - 1; i >= 0; i--) {
      const q = ptr[i];
      q.life -= fdt; if (q.life <= 0) { ptr.splice(i, 1); continue; }
      const f = q.life / q.max;
      const s = this.poolAdd.next(glowDotTex('#ff9c44', p.radius * 0.7));
      s.position.set(q.x, q.y); s.alpha = 0.20 * f * pAlpha; s.scale.set(0.45 + 0.55 * f);
    }

    // ---- говорящая фигура у тайника/источника (§12): пепельный призрак с тлеющим угольком, колышется ----
    if (Game.speak) {
      const sp = Game.speak;
      const pulse = 0.85 + 0.15 * Math.sin(t * 2.2);
      const wob = 1 + Math.sin(t * 4) * 0.08;
      const halo = this.poolAdd.next(lanternTex());
      halo.tint = 0x6a6072; halo.position.set(sp.x, sp.y); halo.scale.set(1.7 * pulse); halo.alpha = 0.42 * pulse;
      const fig = this.poolShapes.next(glowShapeTex('#8a8296', 26, 'vortex'));
      fig.position.set(sp.x, sp.y - 6); fig.rotation = t * 0.5; fig.scale.set(wob, 1.06 / wob); fig.alpha = 0.92;
      const fcore = this.poolAdd.next(glowDotTex('#ffae6a', 6));
      fcore.position.set(sp.x, sp.y - 6); fcore.scale.set(pulse); fcore.alpha = 0.72;   // тлеющий уголёк «в груди»
    }

    // ---- частицы (additive) ----
    for (const pt of Game.particles.active) {
      if (pt.dead) continue;
      const s = this.poolAdd.next(glowDotTex(pt.color, pt.size));
      s.position.set(pt.x, pt.y); s.scale.set(pt.fade); s.alpha = pt.fade;
    }

    // ---- пятна Якорей (под тьмой) ----
    for (const z of Game.darkZones) {
      const a = clamp(z.life / z.maxLife, 0, 1);
      const s = this.poolZones.next(darkZoneTex());
      s.position.set(z.x, z.y); s.scale.set(z.r / 64); s.alpha = a;
      this.zoneRings.circle(z.x, z.y, z.r).stroke({ width: 2, color: 0x783cdc, alpha: 0.22 * a });
    }

    // ---- Святилища (маяки света вдали — additive свечение + кольцо-пульс) ----
    if (Game.shrines) for (const sh of Game.shrines) {
      const pulse = 0.7 + 0.3 * Math.sin(t * 3 + sh.born);
      const cn = colorNum(sh.def.color);
      // вертикальный СТОЛБ-маяк вверх (HG-19): «иди сюда», зовёт издалека и тянет двигаться
      for (let b = 1; b <= 6; b++) {
        const bd = this.poolAdd.next(glowDotTex(sh.def.color, Math.max(2, 9 - b)));
        bd.position.set(sh.x, sh.y - b * 17); bd.alpha = (0.42 - b * 0.055) * pulse; bd.scale.set(0.9, 1.5);
      }
      const halo = this.poolAdd.next(lanternTex());
      halo.tint = cn; halo.position.set(sh.x, sh.y); halo.scale.set(1.8 * pulse); halo.alpha = 0.6 * pulse;
      const core = this.poolAdd.next(glowDotTex(sh.def.color, 12));
      core.position.set(sh.x, sh.y); core.scale.set(pulse); core.alpha = 1;
      this.effectsGfx.circle(sh.x, sh.y, CONFIG.shrines.radius + 8)
        .stroke({ width: 2.5, color: cn, alpha: 0.6 * pulse });
    }

    // ---- ТЕНЁТА: kindle — зона розжига (диск растёт); defend — ядро-очаг с целостностью (краснеет под уроном) ----
    if (Game.snareT > 0) {
      const pul = 0.6 + 0.4 * Math.sin(t * 4);
      if (Game.snareMode === 'defend') {
        const hp = Game.snareCoreMaxHp > 0 ? Math.max(0, Game.snareCoreHp / Game.snareCoreMaxHp) : 0;
        const cr = CONFIG.snare.defend.coreHitRadius;
        // danger-кольцо вокруг ядра — «держи чистым», ярче/краснее когда ядро под давлением
        this.zoneRings.circle(Game.snareX, Game.snareY, cr)
          .stroke({ width: 3, color: 0xff5a3c, alpha: (0.3 + 0.35 * (1 - hp)) * pul });
        // ядро: золотое при здоровье → тускло-красное у смерти; сжимается по мере падения HP
        const ko = this.poolAdd.next(glowDotTex(hp > 0.45 ? '#ffd24a' : '#ff5a3c', 14));
        ko.position.set(Game.snareX, Game.snareY); ko.scale.set((0.55 + 0.7 * hp) * pul); ko.alpha = 0.5 + 0.5 * hp;
      } else {
        const k = Math.max(0, Math.min(1, Game.snareKindle || 0));
        const zr = CONFIG.snare.zoneRadius;
        // граница зоны — кольцо «стой здесь», ярче по мере розжига
        this.zoneRings.circle(Game.snareX, Game.snareY, zr)
          .stroke({ width: 3, color: 0xffb45c, alpha: (0.28 + 0.4 * k) * pul });
        // заполнение розжига — внутренний жаркий диск, растёт от центра к границе
        this.zoneRings.circle(Game.snareX, Game.snareY, zr * (0.18 + 0.82 * k))
          .fill({ color: 0xff7a2c, alpha: 0.07 + 0.13 * k });
        // ядро-очаг в центре — additive свечение, разгорается с розжигом
        const ko = this.poolAdd.next(glowDotTex('#ffd24a', 12));
        ko.position.set(Game.snareX, Game.snareY); ko.scale.set((0.8 + 0.7 * k) * pul); ko.alpha = 0.6 + 0.4 * k;
      }
    }

    // ---- ПРОТОТИП свет-маятника: УГЛИ (стационарные турели из билда) — нимб-свет + кольцо рабочей зоны ----
    if (Game.embers && Game.embers.length) {
      const er = CONFIG.snare.defend.emberRadius;
      const epul = 0.7 + 0.3 * Math.sin(t * 5);
      for (const em of Game.embers) {
        // рабочая зона — кольцо «уголёк видит/бьёт тут» (моат «ты=свет» в статике)
        this.zoneRings.circle(em.x, em.y, er).stroke({ width: 1.5, color: 0xffb45c, alpha: 0.16 * epul });
        // нимб-свет вокруг угля (дешёвый свет без шейдера)
        const halo = this.poolAdd.next(lanternTex());
        halo.tint = 0xffb04a; halo.position.set(em.x, em.y); halo.scale.set(er / 64 * 0.9); halo.alpha = 0.18 * epul;
        // ядро угля
        const ec = this.poolAdd.next(glowDotTex('#ffd86a', 9));
        ec.position.set(em.x, em.y); ec.scale.set(epul); ec.alpha = 0.95;
      }
    }

    // ---- ТЬМА: юниформы (UV-доли) ----
    const cuX = 0.5 + Game.shakeX / W, cuY = 0.5 + Game.shakeY / H, rad = p.light / H;
    this.darkU.uCenter.value[0] = cuX; this.darkU.uCenter.value[1] = cuY;
    this.darkU.uRadius.value = rad;
    this.darkU.uTime.value = t;
    this.darkU.uHpFrac.value = p.hp / p.maxHp;
    this.darkU.uCycle.value = Game.dayNightMult ? (Game.dayNightMult() - 1) / CONFIG.cycle.swing : 0;
    this.gloU.uCenter.value[0] = cuX; this.gloU.uCenter.value[1] = cuY;
    this.gloU.uRadius.value = rad;
    this.gloU.uTime.value = t;

    // ---- глинты (поверх тьмы) + подсчёт угрозы ----
    this.drawGlints(p, W, H, t);

    this.poolShapes.end(); this.poolAdd.end(); this.poolZones.end(); this.poolGlints.end();
  },

  // ПОЛ-ПЕПЕЛИЩЕ: остывший потрескавшийся уголь вместо цифровой сетки.
  // Процедурные изломанные трещины (мировые координаты, стабильны при движении камеры);
  // часть — тлеющие тёплым; редкие угольки-крапинки. Две stroke-партии + один fill (дёшево).
  drawGrid(p: any, W: number, H: number) {
    const g = this.gridGfx;
    g.clear();
    const gap = 150;
    const cx0 = Math.floor((p.x - W / 2 - gap) / gap), cx1 = Math.ceil((p.x + W / 2 + gap) / gap);
    const cy0 = Math.floor((p.y - H / 2 - gap) / gap), cy1 = Math.ceil((p.y + H / 2 + gap) / gap);
    // партия 1 — холодные трещины остывшего шлака
    for (let cx = cx0; cx <= cx1; cx++) for (let cy = cy0; cy <= cy1; cy++) {
      if (cellRand(cx, cy, 2) < 0.28) continue;   // эти — тёплые, идут во 2-й партии
      crackPath(g, cx, cy, gap);
    }
    g.stroke({ width: 1.2, color: 0x342f3c, alpha: 0.5 });
    // партия 2 — тлеющие тёплым трещины (уголь ещё с жаром внутри)
    for (let cx = cx0; cx <= cx1; cx++) for (let cy = cy0; cy <= cy1; cy++) {
      if (cellRand(cx, cy, 2) >= 0.28) continue;
      crackPath(g, cx, cy, gap);
    }
    g.stroke({ width: 1.5, color: 0x6e3a1e, alpha: 0.42 });
    // редкие тёплые угольки-крапинки в золе
    for (let cx = cx0; cx <= cx1; cx++) for (let cy = cy0; cy <= cy1; cy++) {
      if (cellRand(cx, cy, 5) > 0.84) {
        const ex = cx * gap + cellRand(cx, cy, 6) * gap, ey = cy * gap + cellRand(cx, cy, 7) * gap;
        g.circle(ex, ey, 1.6);
      }
    }
    g.fill({ color: 0x8a4a24, alpha: 0.5 });
    // ВЫЖЖЕННАЯ ЗЕМЛЯ — скорч-визуал территории УБРАН по запросу (механика жива невидимо, см. game.ts).
    // нагар-останки на местах смертей — мир копит остатки; тёплый скорч расширяется остывая и гаснет (~5с)
    const marks = Game.ashMarks;
    if (marks && marks.length) {
      const gt = Game.time;
      for (const m of marks) {
        const age = (gt - m.born) / 5;
        if (age < 0 || age >= 1) continue;
        g.circle(m.x, m.y, (m.big ? 16 : 8) + age * 6).fill({ color: 0x4a2616, alpha: 0.32 * (1 - age) });
      }
    }
  },

  // АМБИЕНТНЫЙ ПЕПЕЛ: воздух, полный дрейфующей материи (пепел оседает, угли всплывают).
  // Экранное поле вокруг камеры; ярче у пламени, тускнеет в холодной дали. Чисто визуал.
  drawAmbient(p: any, W: number, H: number) {
    const pool = this.poolAmbient; pool.begin();
    const dt = this._frameDt || 0.016;
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const maxR = Math.hypot(W, H) * 0.5 + 120;
    const motes = this.ashMotes;
    if (motes.length === 0) {
      for (let i = 0; i < 90; i++) {
        const ang = Math.random() * TAU, r = Math.random() * maxR;
        motes.push({ x: p.x + Math.cos(ang) * r, y: p.y + Math.sin(ang) * r,
          vx: rnd(-12, 12), vy: rnd(-10, 6), ember: Math.random() < 0.28,
          size: rnd(1.2, 2.6), ph: Math.random() * TAU });
      }
    }
    const sx0 = W / 2 - p.x + Game.shakeX, sy0 = H / 2 - p.y + Game.shakeY;
    const t = Game.time;
    for (const m of motes) {
      if (m.ember) m.vy -= 7 * dt;                       // угли всплывают
      m.x += m.vx * dt; m.y += m.vy * dt;
      let dx = m.x - p.x, dy = m.y - p.y, d = Math.hypot(dx, dy);
      if (d > maxR) {                                    // ушёл за край — респавн с дальней стороны
        const ang = Math.atan2(dy, dx) + Math.PI + rnd(-0.6, 0.6);
        m.x = p.x + Math.cos(ang) * maxR * 0.95; m.y = p.y + Math.sin(ang) * maxR * 0.95;
        m.vx = rnd(-12, 12); m.vy = m.ember ? rnd(-6, 2) : rnd(-10, 6);
        dx = m.x - p.x; dy = m.y - p.y; d = Math.hypot(dx, dy);
      }
      const near = clamp(1 - d / (p.light * 1.5), 0, 1);   // ярче у пламени
      const flick = 0.75 + 0.25 * Math.sin(t * 3 + m.ph);
      const a = (m.ember ? 0.5 : 0.3) * (0.18 + 0.82 * near) * flick;
      if (a < 0.02) continue;
      const s = pool.next(glowDotTex(m.ember ? '#ff8a3a' : '#9a8f80', 3));
      s.position.set(m.x + sx0, m.y + sy0); s.alpha = a; s.scale.set(m.size / 2.4);
    }
    pool.end();
  },

  drawGlints(p: any, W: number, H: number, t: number) {
    const L = CONFIG.light;
    const phase = Game.time % L.flashPeriod;
    const flashI = phase < L.flashDur ? Math.sin(phase / L.flashDur * Math.PI) : 0;
    let darkThreat = 0, tdx = 0, tdy = 0;
    const tr = L.threatRange;
    for (const e of Game.enemies.active) {
      if (e.dead) continue;
      const X = e.x - p.x + W / 2, Y = e.y - p.y + H / 2;
      if (X < -20 || X > W + 20 || Y < -20 || Y > H + 20) continue;
      const d = dist(e.x, e.y, p.x, p.y);
      const inDark = d > p.light;
      if (inDark && d < p.light + tr) {
        darkThreat++;
        const inv = 1 / Math.max(d, 1);   // направление давления (для «нервного» ореола §8.3)
        tdx += (e.x - p.x) * inv; tdy += (e.y - p.y) * inv;
      }
      if (e.isBoss) {
        const s = this.poolGlints.next(glowDotTex(e.color, L.glintRadius * 2.4));
        s.position.set(e.x, e.y); s.alpha = inDark ? 0.4 : L.glintAlpha;
      } else if (inDark && flashI > 0.01) {
        const r = e.radius > 18 ? L.glintRadius * 1.6 : L.glintRadius;
        const s = this.poolGlints.next(glowDotTex(e.color, r));
        s.position.set(e.x, e.y); s.alpha = L.glintAlpha * flashI;
      }
    }
    // пульс угрозы (в шейдере ореола): a = (0.05+0.12*inten)*(0.5+0.5*puls)
    let threatA = 0;
    if (darkThreat > 0) {
      const inten = Math.min(1, darkThreat / 16);
      const puls = 0.5 + 0.5 * Math.sin(Game.time * 5);
      threatA = (0.05 + 0.12 * inten) * (0.5 + 0.5 * puls);
    }
    this.gloU.uThreatA.value = threatA;
    // «нервный» наклон ореола к стороне сильнейшего давления (если давление несимметрично)
    const tlen = Math.hypot(tdx, tdy);
    if (tlen > 0.001 && darkThreat > 0) {
      const lean = Math.min(1, darkThreat / 14) * 0.85;
      this.gloU.uThreatDir.value[0] = (tdx / tlen) * lean;
      this.gloU.uThreatDir.value[1] = (tdy / tlen) * lean;
    } else {
      this.gloU.uThreatDir.value[0] = 0; this.gloU.uThreatDir.value[1] = 0;
    }

    // святилища — маяки, видны сквозь тьму (поверх dark-меша)
    if (Game.shrines) for (const sh of Game.shrines) {
      const X = sh.x - p.x + W / 2, Y = sh.y - p.y + H / 2;
      if (X < -20 || X > W + 20 || Y < -20 || Y > H + 20) continue;
      const s = this.poolGlints.next(glowDotTex(sh.def.color, 7));
      s.position.set(sh.x, sh.y); s.alpha = 0.9;
    }

    // искры и снаряды видны в темноте. ОПЫТ во тьме тускнеет с расстоянием (карта не пестрит) +
    // гаснет к концу жизни. Золото — яркий маяк (не гаснет, не тускнеет).
    for (const k of Game.pickups.active) {
      if (k.dead) continue;
      const X = k.x - p.x + W / 2, Y = k.y - p.y + H / 2;
      if (X < -20 || X > W + 20 || Y < -20 || Y > H + 20) continue;
      const s = this.poolGlints.next(glowDotTex(k.color, k.type === 'xpbig' ? 5 : k.type === 'gold' ? 4 : 3));
      s.position.set(k.x, k.y);
      let a = 0.85;
      if (k.type !== 'gold') {
        const dk = Math.hypot(k.x - p.x, k.y - p.y);
        if (dk > p.light) a = clamp(0.12 + 0.73 * (1 - (dk - p.light) / 200), 0.05, 0.85);   // дальше во тьме — тусклее
        if (k.life > 0) a *= clamp(k.life / 3, 0.12, 1);                                       // остывает
      }
      s.alpha = a;
    }
    for (const pr of Game.projectiles.active) {
      if (pr.dead || pr.kind === 'mine' || pr.kind === 'lantern') continue;
      const X = pr.x - p.x + W / 2, Y = pr.y - p.y + H / 2;
      if (X < -20 || X > W + 20 || Y < -20 || Y > H + 20) continue;
      const s = this.poolGlints.next(glowDotTex(pr.color, L.glintRadius));
      s.position.set(pr.x, pr.y); s.alpha = pr.hostile ? L.shotGlintAlpha : L.glintAlpha;
    }
  },
};

// '#rrggbb' → 0xRRGGBB
const _colorCache = new Map<string, number>();
function colorNum(c: string): number {
  let n = _colorCache.get(c);
  if (n !== undefined) return n;
  n = (c[0] === '#') ? parseInt(c.slice(1), 16) : 0xffffff;
  if (c.length === 4) { // #rgb
    const r = parseInt(c[1], 16), g = parseInt(c[2], 16), b = parseInt(c[3], 16);
    n = (r * 17 << 16) | (g * 17 << 8) | (b * 17);
  }
  _colorCache.set(c, n);
  return n;
}
// осветлённый кант цвета врага — насыщенная цветная обводка типа (lerp к белому), кэш per-число
const _rimCache = new Map<number, number>();
function rimColor(n: number): number {
  let v = _rimCache.get(n);
  if (v !== undefined) return v;
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff, f = 0.42;
  v = (Math.round(r + (255 - r) * f) << 16) | (Math.round(g + (255 - g) * f) << 8) | Math.round(b + (255 - b) * f);
  _rimCache.set(n, v);
  return v;
}
// БЕЛО-ХОЛОДНЫЙ блум-кант врага: тянем цвет типа к морозному белому (235,242,255) — доминирует
// холодное свечение, hue типа остаётся лишь намёком (различение типов не теряется). Кэш per-число.
const _coolRimCache = new Map<number, number>();
function coolRim(n: number): number {
  let v = _coolRimCache.get(n);
  if (v !== undefined) return v;
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff, f = 0.6;
  v = (Math.round(r + (235 - r) * f) << 16) | (Math.round(g + (242 - g) * f) << 8) | Math.round(b + (255 - b) * f);
  _coolRimCache.set(n, v);
  return v;
}
function wdef(key: string): any { return CONFIG.weapons[key] || CONFIG.evolutions[key]; }

// детерминированный hash ячейки → [0,1) (стабильные процедурные трещины пола в мировых координатах)
function cellRand(cx: number, cy: number, salt: number): number {
  let h = (cx * 374761393 + cy * 668265263 + salt * 1274126177) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}
// изломанная трещина в ячейке: 2-3 сегмента со случайным дрейфом угла. Добавляет subpath в Graphics.
function crackPath(g: Graphics, cx: number, cy: number, gap: number) {
  let x = cx * gap + cellRand(cx, cy, 0) * gap;
  let y = cy * gap + cellRand(cx, cy, 1) * gap;
  let ang = cellRand(cx, cy, 3) * TAU;
  const segs = 2 + Math.floor(cellRand(cx, cy, 4) * 2);
  g.moveTo(x, y);
  for (let s = 0; s < segs; s++) {
    const len = 34 + cellRand(cx, cy, 10 + s) * 64;
    ang += (cellRand(cx, cy, 20 + s) - 0.5) * 1.5;
    x += Math.cos(ang) * len; y += Math.sin(ang) * len;
    g.lineTo(x, y);
  }
}

// линейная интерполяция двух 0xRRGGBB
function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const r = Math.round(ar + (br - ar) * t), g = Math.round(ag + (bg - ag) * t), bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
// температура ядра-пламени по «живости» (доля света): 1=бело-голубой жар → багровый уголь при 0 (§10.1)
const CORE_STOPS: [number, number][] = [[0.0, 0x8b1a00], [0.28, 0xff8c2a], [0.55, 0xffd24a], [1.0, 0xfff8f0]];
function coreTempTint(f: number): number {
  f = f < 0 ? 0 : f > 1 ? 1 : f;
  for (let i = 1; i < CORE_STOPS.length; i++) {
    if (f <= CORE_STOPS[i][0]) {
      const s0 = CORE_STOPS[i - 1], s1 = CORE_STOPS[i];
      return lerpColor(s0[1], s1[1], (f - s0[0]) / (s1[0] - s0[0]));
    }
  }
  return CORE_STOPS[CORE_STOPS.length - 1][1];
}

// локальные вершины силуэта-контура по архетип-форме (игроки + враги). null = круг. (HG-2/HG-3)
function shapePolys(shape: string, r: number): number[][] | null {
  switch (shape) {
    // ---- формы героев ----
    case 'tri':    return [[0, -r * 1.12], [r * 0.96, r * 0.74], [-r * 0.96, r * 0.74]];      // ember — язык вверх
    case 'hex': {                                                                              // umbra — гранёный уголь
      const o: number[][] = [];
      for (let i = 0; i < 6; i++) { const a = -Math.PI / 2 + i * Math.PI / 3; o.push([Math.cos(a) * r, Math.sin(a) * r]); }
      return o;
    }
    case 'circle': return null;                                                                // mirror — круг
    case 'square': return [[-r * 0.8, -r * 0.8], [r * 0.8, -r * 0.8], [r * 0.8, r * 0.8], [-r * 0.8, r * 0.8]];
    // ---- формы врагов (HG-3: дифференциация опасности силуэтом) ----
    case 'shard':  return [[0, -r * 1.25], [r * 0.42, 0], [r * 0.16, r], [-r * 0.16, r], [-r * 0.42, 0]]; // рой — острый осколок-шип
    case 'block': {                                                                            // танк — массивный срезанный блок (октагон)
      return [[-r * 0.55, -r], [r * 0.55, -r], [r, -r * 0.55], [r, r * 0.55], [r * 0.55, r], [-r * 0.55, r], [-r, r * 0.55], [-r, -r * 0.55]];
    }
    case 'streak': return [[-r * 1.35, 0], [0, -r * 0.5], [r * 1.35, 0], [0, r * 0.5]];        // быстрый — горизонтальная смазка
    case 'vortex': {                                                                            // босс — вихрь-звезда (6 лучей), читается издали
      const o: number[][] = [];
      for (let i = 0; i < 12; i++) { const a = -Math.PI / 2 + i * Math.PI / 6; const rr = i % 2 ? r * 0.48 : r; o.push([Math.cos(a) * rr, Math.sin(a) * rr]); }
      return o;
    }
    default:       return [[0, -r * 1.15], [r * 0.7, 0], [0, r], [-r * 0.7, 0]];               // spark — искра-ромб
  }
}

// обвести polygon-путь на Graphics с поворотом и неравномерным масштабом (контур врага/игрока). pts=null → caller рисует круг сам.
function tracePoly(g: Graphics, cx: number, cy: number, pts: number[][] | null, rot: number, sx: number, sy: number): boolean {
  if (!pts) return false;
  const cr = Math.cos(rot), sr = Math.sin(rot);
  for (let i = 0; i < pts.length; i++) {
    const lx = pts[i][0] * sx, ly = pts[i][1] * sy;
    const wx = cx + (lx * cr - ly * sr), wy = cy + (lx * sr + ly * cr);
    if (i === 0) g.moveTo(wx, wy); else g.lineTo(wx, wy);
  }
  g.closePath();
  return true;
}
